import {makeAutoObservable, runInAction} from "mobx";

const supportedImageTypes = ["png", "jpg", "jpeg"];
const imageMaxWidthAndHeight = 4000;
const fileSizeLimit = 5242880;
const fileReader = new FileReader();

export class ImageObservable {
  fileName: string;
  validate?: (toValidate: Blob | null) => boolean;
  isDirty: boolean;
  file: Blob | null;
  hasNewUpload: boolean;
  fileId: string | null;
  private _hasExceedsFileSizeLimit: boolean = false;
  private _isInvalidFileFormat: boolean = false;
  private _hasExceedsResolution: boolean = false;

  constructor(
    fileId?: string,
    validate?: (toValidate: Blob | null) => boolean
  ) {
    makeAutoObservable(this);
    this.file = null;
    this.fileName = "";
    this.fileId = fileId ?? null;
    this.validate = validate;
    this.isDirty = false;
    this.hasNewUpload = false;
  }

  setIsDirty = (value: boolean) => {
    this.isDirty = value;
  };

  get isValid() {
    return (
      (this.validate == null || !this.isDirty || this.validate(this.file)) &&
      !this.hasExceedsFileSizeLimit &&
      !this.isInvalidFileFormat &&
      !this._hasExceedsResolution
    );
  }

  get hasExceedsFileSizeLimit() {
    return this._hasExceedsFileSizeLimit;
  }

  get isInvalidFileFormat() {
    return this._isInvalidFileFormat;
  }

  get hasExceedsResolution() {
    return this._hasExceedsResolution;
  }

  setImageFromUpload = async (file: File) => {
    this._hasExceedsFileSizeLimit = false;
    this._isInvalidFileFormat = false;
    this._hasExceedsResolution = false;

    this.file = file;
    this.fileId = "";
    this.hasNewUpload = true;
    this.fileName = file.name;
    this.setIsDirty(true);

    const fileExtension =
      this.fileName?.substring(
        this.fileName?.lastIndexOf(".") + 1,
        this.fileName?.length
      ) ?? "";

    this._isInvalidFileFormat = !supportedImageTypes.includes(fileExtension);

    if (file.size > fileSizeLimit) {
      this._hasExceedsFileSizeLimit = true;
    }

    const isWithinPixelDimensions = await this.isImageWithinPixelDimensions(
      file
    );

    runInAction(() => {
      this._hasExceedsResolution = !isWithinPixelDimensions;
    });
  };

  setImageFromDownload = (file: Blob) => {
    this.file = file;
    this.hasNewUpload = false;
    this.fileName = "";
    this.setIsDirty(false);
  };

  setFileId = (fileId: string) => {
    this.fileId = fileId;
    this.setIsDirty(true);
  };

  resetFile = () => {
    this.file = null;
    this.fileName = "";
    this.fileId = null;
    this._isInvalidFileFormat = false;
    this.setIsDirty(true);
  };

  // No good way to test this
  // TODO refactor to use similar code to ImageEditorObservable
  // DJ is cautious of memory leaks with the fileReader being left open
  /* istanbul ignore next */
  private isImageWithinPixelDimensions = async (file: File) => {
    return new Promise<boolean>((resolve) => {
      // Not the most ideal way of doing this but the only way that has worked

      //Read the contents of Image File.
      fileReader.onload = () => {}; //clear of previous action, as we must use the new resolve param
      fileReader.readAsDataURL(file);
      fileReader.onload = function (e) {
        let image = new Image();

        //Set the Base64 string return from FileReader as source.
        // @ts-ignore
        image.src = e.target.result;

        //Validate the File Height and Width.
        image.onload = function () {
          if (
            // @ts-ignore
            this.height > imageMaxWidthAndHeight ||
            // @ts-ignore
            this.width > imageMaxWidthAndHeight
          ) {
            image.src = "";
            resolve(false);
            return;
          }
          image.src = "";
          resolve(true);
          return;
        };
      };
    });
  };
}
